import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as _ from 'lodash';
import { environment } from 'src/environments/environment';
import { Assignment, AssignmentResults, Section, Question } from '../../_models';

@Injectable({
  providedIn: 'root'
})
export class AssignmentService {
  private apiUrl = `${environment.apiUrl}/Assignment`;
  constructor(private http: HttpClient) {
  }

  getById(assignmentId: number): Observable<Assignment> {
    return this.http.get<Assignment>(`${this.apiUrl}/GetById/${assignmentId}`)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }

  getByIdWithAssignmentQuestions(assignmentId: number): Observable<Assignment> {
    return this.http.get<Assignment>(`${this.apiUrl}/GetByIdWithAssignmentQuestions/${assignmentId}`)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }

  getByIdWithStatistic(assignmentId: number): Observable<Assignment> {
    return this.http.get<Assignment>(`${this.apiUrl}/GetByIdWithStatistic/${assignmentId}`)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }

  getByIdWithAssignmentQuestionsAndOptionScoresWithPossibleScores(assignmentId: number): Observable<Assignment> {
    return this.http.get<Assignment>(`${this.apiUrl}/GetByIdWithAssignmentQuestionsAndOptionScoresWithPossibleScores/${assignmentId}`)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }

  getBySectionSubjectIdWithStatistic(sectionSubjectId: number): Observable<Assignment[]> {
    return this.http.get<Assignment[]>(`${this.apiUrl}/GetBySectionSubjectIdWithStatistic/${sectionSubjectId}`)
      .pipe(map(q => {
        _.forEach(q, qz => this.setAssignmentDates(qz));
        return q;
      }));
  }

  getAssignmentResults(assignmentId: number): Observable<AssignmentResults> {
    return this.http.get<AssignmentResults>(`${this.apiUrl}/GetAssignmentResults/${assignmentId}`);
  }


  getByIdWithSectionSubjectsAndStudents(assignmentId: number): Observable<Assignment> {
    return this.http.get<Assignment>(`${this.apiUrl}/GetByIdWithSectionSubjectsAndStudents/${assignmentId}`)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }


  ///////////////

  addAssignment(assignment: Assignment): Observable<Assignment> {
    return this.http.post<Assignment>(`${this.apiUrl}/AddAssignment`, assignment)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }

  updateAssignmentGeneral(assignment: Assignment): Observable<Assignment> {
    return this.http.put<Assignment>(`${this.apiUrl}/UpdateAssignmentGeneral`, assignment)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }

  updateAssignmentScoreTypes(assignment: Assignment): Observable<Assignment> {
    return this.http.put<Assignment>(`${this.apiUrl}/UpdateAssignmentScoreTypes`, assignment)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }

  updateAssignmentScoresDisplay(assignment: Assignment): Observable<Assignment> {
    return this.http.put<Assignment>(`${this.apiUrl}/UpdateAssignmentScoresDisplay`, assignment)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }

  updateAssignmentAvailability(assignment: Assignment): Observable<Assignment> {
    return this.http.put<Assignment>(`${this.apiUrl}/UpdateAssignmentAvailability`, assignment)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }

  updateAssignmentSetup(assignment: Assignment): Observable<Assignment> {
    return this.http.put<Assignment>(`${this.apiUrl}/UpdateAssignmentSetup`, assignment)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }

  updateAssignmentAdvanced(assignment: Assignment): Observable<Assignment> {
    return this.http.put<Assignment>(`${this.apiUrl}/updateAssignmentAdvanced`, assignment)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }

  endAssignment(assignmentId: number): Observable<Assignment> {
    return this.http.put<Assignment>(`${this.apiUrl}/EndAssignment/${assignmentId}`, {})
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }

  deleteAssignment(assignmentId: number, password: string): Observable<Assignment> {
    return this.http.post<Assignment>(`${this.apiUrl}/DeleteAssignment`, { entityId: assignmentId, password })
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }

  addAssignmentQuestion(assignmentId: number, questionId: number): Observable<Question> {
    return this.http.put<Question>(`${this.apiUrl}/AddAssignmentQuestion/${assignmentId}/${questionId}`, {});
  }

  deleteAssignmentQuestion(assignmentId: number, questionId: number): Observable<Question> {
    return this.http.delete<Question>(`${this.apiUrl}/DeleteAssignmentQuestion/${assignmentId}/${questionId}`);
  }

  updateQuestionsOrder(assignmentId: number, questionsOrder: { key: number, value: number }[]): Observable<{ key: number, value: number }[]> {
    return this.http.put<{ key: number, value: number }[]>(`${this.apiUrl}/UpdateQuestionsOrder/${assignmentId}`, questionsOrder);
  }


  getByIdWithSectionSubjectsAndStudentsAndAttachmentsAndLinks(assignmentId: number): Observable<Assignment> {
    return this.http.get<Assignment>(`${this.apiUrl}/GetByIdWithSectionSubjectsAndStudentsAndAttachmentsAndLinks/${assignmentId}`)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));
  }

  addRegularAssignment(assignment: Assignment): Observable<Assignment> {

    return this.http.post<Assignment>(`${this.apiUrl}/AddRegularAssignment`, assignment)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));

    // const formData: FormData = new FormData();
    // if (assignment.files && assignment.files.length > 0) {
    //   for (let j = 0; j < assignment.files.length; j++) {
    //     formData.append('files', assignment.files[j], assignment.files[j].name);
    //   }
    // }

    // formData.append('assignmentId', assignment.assignmentId + '');
    // formData.append('type', assignment.type + '');
    // formData.append('title', assignment.title ? assignment.title : '');
    // formData.append('description', assignment.description ? assignment.description : '');
    // formData.append('isAssignedToStudents', assignment.isAssignedToStudents + '');
    // formData.append('status', assignment.status + '');
    // formData.append('endDateTime', assignment.endDateTime ? assignment.endDateTime.toISOString() : '');
    // formData.append('assessmentId', assignment.assessmentId ? assignment.assessmentId + '' : '');
    // formData.append('cceSubCriteriaId', assignment.cceSubCriteriaId ? assignment.cceSubCriteriaId + '' : '');
    // formData.append('sectionSubjectsIdsJson', JSON.stringify(assignment.sectionSubjectsIds));
    // formData.append('studentsIdsJson', JSON.stringify(assignment.studentsIds));
    // formData.append('attachmentsJson', JSON.stringify(assignment.attachments));
    // formData.append('linksJson', JSON.stringify(assignment.links));


  }

  addBulkRegularAssignments(assignments: Assignment[]): Observable<Assignment[]> {
    return this.http.post<Assignment[]>(`${this.apiUrl}/AddBulkRegularAssignments`, assignments);
  }

  updateRegularAssignment(assignment: Assignment): Observable<Assignment> {

    return this.http.put<Assignment>(`${this.apiUrl}/UpdateRegularAssignment`, assignment)
      .pipe(map(q => {
        this.setAssignmentDates(q);
        return q;
      }));

    // const formData: FormData = new FormData();
    // if (assignment.files && assignment.files.length > 0) {
    //   for (let j = 0; j < assignment.files.length; j++) {
    //     formData.append('files', assignment.files[j], assignment.files[j].name);
    //   }
    // }

    // formData.append('assignmentId', assignment.assignmentId + '');
    // formData.append('type', assignment.type + '');
    // formData.append('title', assignment.title ? assignment.title : '');
    // formData.append('description', assignment.description ? assignment.description : '');
    // formData.append('isAssignedToStudents', assignment.isAssignedToStudents + '');
    // formData.append('status', assignment.status + '');
    // formData.append('endDateTime', assignment.endDateTime ? assignment.endDateTime.toISOString() : '');
    // formData.append('assessmentId', assignment.assessmentId ? assignment.assessmentId + '' : '');
    // formData.append('cceSubCriteriaId', assignment.cceSubCriteriaId ? assignment.cceSubCriteriaId + '' : '');
    // formData.append('sectionSubjectsIdsJson', JSON.stringify(assignment.sectionSubjectsIds));
    // formData.append('studentsIdsJson', JSON.stringify(assignment.studentsIds));
    // formData.append('attachmentsJson', JSON.stringify(assignment.attachments));
    // formData.append('linksJson', JSON.stringify(assignment.links));

    // return this.http.put<Assignment>(`${this.apiUrl}/UpdateRegularAssignment`, formData)
    //   .pipe(map(q => {
    //     this.setAssignmentDates(q);
    //     return q;
    //   }));
  }

  private setAssignmentDates(assignment: Assignment) {
    if (!assignment)
      return;

    if ((typeof assignment.startDateTime) === 'string' && String(assignment.startDateTime).length > 0)
      assignment.startDateTime = new Date(String(assignment.startDateTime));

    if ((typeof assignment.endDateTime) === 'string' && String(assignment.endDateTime).length > 0)
      assignment.endDateTime = new Date(String(assignment.endDateTime));

    if ((typeof assignment.cutOffDateTime) === 'string' && String(assignment.cutOffDateTime).length > 0)
      assignment.cutOffDateTime = new Date(String(assignment.cutOffDateTime));

    // if ((typeof assignment.displayResultDateTime) === 'string' && String(assignment.displayResultDateTime).length > 0)
    //   assignment.displayResultDateTime = new Date(String(assignment.displayResultDateTime));
  }

}
